import { Timestamp } from 'firebase/firestore';
import React, { useState } from 'react';
import { Route, Routes, useParams } from 'react-router-dom';
import { useMaterialsStore } from '../../components/Stores/MaterialsStore';
import { useAccountStore } from '../../components/Stores/accountStore';
import { StepsMenu } from '../../components/UI-Elements/Flow/Steps/StepsMenu';
import { updateBalanceFlow } from '../../sp-firebase/balanceFlowFB';
import { useBalanceFlow } from '../../hooks/useBalanceFlow';
import { IBalanceFlow, IBalanceFlowProps } from '../../sp-types/balanceFlowType';
import { BalanceFlowSkeleton } from './BalanceFlowSkeleton';
import TimeLeftUntil from './TimeLeftUntil';
import { balanceStepDictionary, getMenuStepsDataFromBalanceFlow } from './balanceUtils';
import { LoadingSpinnerOverlay } from '../../components/UI-Elements/LoadingSpinnerOverlay';
import { MaterialTrendButton } from '../Dashboard/MaterialTrendButton';
import { getMaterialsCardData } from '../Dashboard/dashboardUtils';
import { useExposureStore } from '../../components/Stores/exposureDataStore';
import { EditRoutes } from './EditRoutes';
import { getRoutePoliciesByMaterial } from '../../sp-firebase/routePolicyFB';
import { IRoute, IRoutePolicy } from '../../sp-types';
import { getItemsOfMaterial, sortByOrder } from '../Policy/MaterialDetails/components/MaterialDetailUtils';
import { getAllRoutes, getRoutesByIds } from '../../sp-firebase/routesFB';
import { useCurrencyRates } from '@hooks/useCurrencyRates';

export const BalanceFlowScreen = () => {
  const { account, materialColorDictionary } = useAccountStore();
  const { netExposure, currentSelectedMaterialId, setCurrentSelectedMaterialId } = useExposureStore();
  const { balanceID } = useParams<{ balanceID: string }>();
  const { data, isLoading: isBalanceLoading, refetch } = useBalanceFlow(balanceID);
  const { materialId } = data || {};

  const { materialsMap } = useMaterialsStore();
  const { convertionMap } = useCurrencyRates();

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const updateAndRefetch = async (balanceID: string, newData: IBalanceFlow) => {
    await updateBalanceFlow(balanceID, newData);
    refetch();
  };

  const setCurrentBalanceStepByName = (name: string) => {
    const index = data?.steps.filter((item) => item.stepName === name);
    index && data && setCurrentStepIndex(index[0].order);
  };

  const [routePolicies, setRoutePolicies] = React.useState<IRoutePolicy[] | undefined>();

  React.useEffect(() => {
    if (account?.id && materialId && balanceID && balanceID === data?.id && materialId === data?.materialId) {
      fetchRoutePolicies(materialId || '');
    }
  }, [account?.id, materialId, balanceID, data?.id]);

  async function fetchRoutePolicies(materialId: string) {
    setIsLoading(true);

    const routePoles = await getRoutePoliciesByMaterial(account?.id || '', materialId);

    setRoutePolicies(routePoles?.sort(sortByOrder));

    if (routePoles?.length) {
      await fetchByPolicies(routePoles.map(({ routeId }) => routeId));
    } else {
      const _allRoutes = await getAllRoutes();

      const materialRoutes = getItemsOfMaterial(_allRoutes || [], materialId);

      setRoutesData(materialRoutes);
    }

    setIsLoading(false);
  }

  const fetchByPolicies = async (routeIds: string[]) => {
    const routes = await getRoutesByIds(routeIds);

    setRoutesData(routes);
  };

  const [routesData, setRoutesData] = React.useState<IRoute[]>();

  return data && !isBalanceLoading ? (
    <div className="flex grow flex-1 flex-col h-full space-y-3 relative" key={balanceID}>
      <MaterialTrendButton
        buttons={getMaterialsCardData(
          materialsMap,
          materialColorDictionary,
          convertionMap,
          account.materialsToAccount.filter((item) => item.materialId === data.materialId),
          netExposure,
        )}
        currentMaterial={materialId || currentSelectedMaterialId}
        setCurrentMaterial={setCurrentSelectedMaterialId}
      />
      <Routes>
        <Route
          path="/edit-routes"
          element={
            <EditRoutes
              routes={[]}
              balanceFlowData={data}
              updateBalance={updateAndRefetch}
              materialsMap={materialsMap}
            />
          }
        ></Route>
        <Route
          path="*"
          element={
            <div className="flex  flex-1 gap-[1.25rem]">
              <div className="">
                <StepsMenu
                  title="Financial balancing"
                  subTitle="Step-by-step"
                  onSelectStep={setCurrentBalanceStepByName}
                  AdditionalComponent={
                    <TimeLeftUntil timestamp={Timestamp.fromDate(new Date('2024-06-03T23:59:59'))} />
                  }
                  singleSteps={getMenuStepsDataFromBalanceFlow(
                    data.steps,
                    data.steps[currentStepIndex].stepName,
                    setCurrentBalanceStepByName,
                  )}
                />
              </div>
              <div className="py-[1.5rem] px-[2.5rem] bg-white flex flex-1 overflow-auto shadow-md">
                {data.steps[currentStepIndex].stepName &&
                  balanceStepDictionary[data.steps[currentStepIndex].stepName] &&
                  routesData && (
                    <BalanceStepComponent
                      step={data.steps[currentStepIndex].stepName}
                      componentProps={{
                        updateBalanceFlowData: (newBalanceFlow: IBalanceFlow) =>
                          updateAndRefetch(data.id, newBalanceFlow),
                        balanceFlowData: data,
                        currentStepIndex: currentStepIndex,
                        setCurrentStepIndex: setCurrentStepIndex,
                        setIsLoading,
                        material: materialsMap[data.materialId],
                        routesData,
                        routePolicies,
                      }}
                    />
                  )}
                {isLoading && <LoadingSpinnerOverlay size={64} />}
                {!routesData && <LoadingSpinnerOverlay size={64}>Loading routes</LoadingSpinnerOverlay>}
              </div>
            </div>
          }
        />
      </Routes>
    </div>
  ) : (
    <BalanceFlowSkeleton />
  );
};

interface Props {
  step: keyof typeof balanceStepDictionary;
  componentProps: IBalanceFlowProps;
}

const BalanceStepComponent: React.FC<Props> = ({ step, componentProps }) => {
  const StepComponent = balanceStepDictionary[step].Component;

  return <StepComponent {...componentProps} key={componentProps.currentStepIndex} />;
};
